import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Upload, ShieldAlert, Users, Award, Accessibility, Scale, Download } from 'lucide-react';

const GENDERS = [
  { v: 'male', l: 'Masculino' }, { v: 'female', l: 'Feminino' },
  { v: 'other', l: 'Outro' }, { v: 'not_informed', l: 'Não informado' },
];
const RACES = [
  { v: 'white', l: 'Branca' }, { v: 'black', l: 'Preta' }, { v: 'brown', l: 'Parda' },
  { v: 'yellow', l: 'Amarela' }, { v: 'indigenous', l: 'Indígena' }, { v: 'not_informed', l: 'Não informado' },
];
const AGES = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const EDUCATIONS = [
  { v: 'fundamental', l: 'Fundamental' }, { v: 'medio', l: 'Médio' }, { v: 'tecnico', l: 'Técnico' },
  { v: 'superior', l: 'Superior' }, { v: 'pos_graduacao', l: 'Pós-graduação' }, { v: 'not_informed', l: 'Não informado' },
];
const LEVELS = [
  { v: 'operational', l: 'Operacional' }, { v: 'technical', l: 'Técnico' },
  { v: 'coordination', l: 'Coordenação' }, { v: 'management', l: 'Gerência' }, { v: 'direction', l: 'Diretoria' },
];
const DISABILITY_TYPES = [
  { v: 'visual', l: 'Visual' }, { v: 'auditory', l: 'Auditiva' }, { v: 'motor', l: 'Motora/Física' },
  { v: 'intellectual', l: 'Intelectual' }, { v: 'multiple', l: 'Múltipla' }, { v: 'psychosocial', l: 'Psicossocial' }, { v: 'other', l: 'Outra' },
];

const EMPTY_EMP = {
  branch_id: '',
  internal_code: '',
  name: '',
  gender: 'not_informed',
  race: 'not_informed',
  age_range: '',
  education: 'not_informed',
  department: '',
  position: '',
  hierarchy_level: '',
  is_leadership: false,
  has_disability: false,
  disability_type: '',
  hire_date: '',
  lgpd_consent: false,
};

const StatCard = ({ icon: Icon, label, value, hint, color = 'teal' }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
          <div className="text-3xl font-bold text-gray-900 mt-1">{value}</div>
          {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 text-${color}-700 flex items-center justify-center`}>
          <Icon size={20} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DistributionBar = ({ label, data, labelMap }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0) || 1;
  const entries = Object.entries(data).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-gray-800">{label}</div>
      {entries.length === 0 ? (
        <div className="text-xs text-gray-500 italic">Sem dados</div>
      ) : entries.map(([k, v]) => {
        const pct = Math.round((v / total) * 100);
        return (
          <div key={k}>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>{labelMap?.[k] || k}</span>
              <span className="font-mono">{v} · {pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function PersonnelPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [employees, setEmployees] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EMP);
  const [deleteId, setDeleteId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/companies/`, { withCredentials: true });
      setCompanies(data);
      if (data.length && !selectedCompany) setSelectedCompany(data[0].id);
    } catch {
      toast.error('Erro ao carregar empresas');
    }
  }, [API_URL, selectedCompany]);

  const loadEmployees = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const [empRes, anRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees/`, { withCredentials: true, params: { company_id: selectedCompany } }),
        axios.get(`${API_URL}/api/employees/analytics/${selectedCompany}`, { withCredentials: true }),
      ]);
      setEmployees(empRes.data);
      setAnalytics(anRes.data);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [API_URL, selectedCompany]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const selectedCompanyObj = useMemo(
    () => companies.find((c) => c.id === selectedCompany),
    [companies, selectedCompany]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lgpd_consent && !editing) {
      toast.error('É necessário confirmar consentimento LGPD');
      return;
    }
    try {
      const payload = { ...form, company_id: selectedCompany };
      if (!payload.branch_id) delete payload.branch_id;
      if (!payload.age_range) delete payload.age_range;
      if (!payload.hierarchy_level) delete payload.hierarchy_level;
      if (!payload.disability_type) delete payload.disability_type;
      if (!payload.hire_date) delete payload.hire_date;
      if (editing) {
        await axios.put(`${API_URL}/api/employees/${editing.id}`, payload, { withCredentials: true });
        toast.success('Funcionário atualizado');
      } else {
        await axios.post(`${API_URL}/api/employees/`, payload, { withCredentials: true });
        toast.success('Funcionário adicionado');
      }
      setDialogOpen(false);
      resetForm();
      loadEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (e) => {
    setEditing(e);
    setForm({
      branch_id: e.branch_id || '',
      internal_code: e.internal_code || '',
      name: e.name || '',
      gender: e.gender || 'not_informed',
      race: e.race || 'not_informed',
      age_range: e.age_range || '',
      education: e.education || 'not_informed',
      department: e.department || '',
      position: e.position || '',
      hierarchy_level: e.hierarchy_level || '',
      is_leadership: !!e.is_leadership,
      has_disability: !!e.has_disability,
      disability_type: e.disability_type || '',
      hire_date: e.hire_date || '',
      lgpd_consent: !!e.lgpd_consent,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/employees/${id}`, { withCredentials: true });
      toast.success('Removido');
      loadEmployees();
    } catch {
      toast.error('Erro ao remover');
    } finally {
      setDeleteId(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const { data } = await axios.post(
        `${API_URL}/api/employees/import?company_id=${selectedCompany}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Importados: ${data.inserted} · Erros: ${data.errors.length}`);
      setImportOpen(false);
      setImportFile(null);
      loadEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na importação');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => { setEditing(null); setForm(EMPTY_EMP); };

  const quotaColor = {
    compliant: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    non_compliant: 'bg-red-100 text-red-800 border-red-200',
    not_applicable: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  const quotaLabel = {
    compliant: 'Em conformidade',
    non_compliant: 'Fora da cota',
    not_applicable: 'Não aplicável (<100)',
  };

  return (
    <AdminSidebar
      title="Quadro de Pessoal"
      subtitle="Análise de diversidade e conformidade legal (Lei de Cotas 8213/91)"
    >
      {/* LGPD Warning */}
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
        <ShieldAlert className="text-amber-600 shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-amber-900">
          <strong>Dados sensíveis LGPD:</strong> raça, deficiência e informações de saúde são dados sensíveis (Art. 5º, II).
          Utilize <strong>códigos internos</strong> ao invés de CPF e obtenha consentimento explícito de cada funcionário antes de cadastrar.
        </div>
      </div>

      {/* Company selector */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label htmlFor="company-select" className="text-xs">Empresa Cliente</Label>
            <select
              id="company-select"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm mt-1"
              data-testid="personnel-company-select"
            >
              <option value="">Selecione...</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.trade_name || c.legal_name}</option>
              ))}
            </select>
          </div>
          {selectedCompany && (
            <>
              <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-csv-button">
                    <Upload className="mr-2 h-4 w-4" /> Importar CSV
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Importar Funcionários (CSV)</DialogTitle>
                    <DialogDescription>
                      Colunas aceitas: internal_code, name, gender, race, age_range, education, department, position, hierarchy_level, is_leadership, has_disability, disability_type, hire_date. Ao importar, você confirma que o consentimento LGPD foi obtido externamente.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <a
                      href={`${API_URL}/api/employees/template.csv`}
                      download="modelo_quadro_pessoal.csv"
                      className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 underline font-medium"
                      data-testid="download-csv-template"
                    >
                      <Download size={14} /> Baixar modelo CSV (com valores aceitos)
                    </a>
                    <Input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      data-testid="csv-file-input"
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setImportOpen(false)}>Cancelar</Button>
                      <Button onClick={handleImport} disabled={!importFile || importing} data-testid="csv-import-confirm">
                        {importing ? 'Importando...' : 'Importar'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-employee-button">
                    <Plus className="mr-2 h-4 w-4" /> Novo Funcionário
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editing ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
                    <DialogDescription>Utilize código interno, evite armazenar CPF.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emp_code">Código interno</Label>
                        <Input id="emp_code" value={form.internal_code} onChange={(e) => setForm({ ...form, internal_code: e.target.value })} data-testid="emp-code-input" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="emp_name">Nome (opcional)</Label>
                        <Input id="emp_name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_branch">Filial</Label>
                        <select id="emp_branch" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                          <option value="">Consolidado (matriz)</option>
                          {(selectedCompanyObj?.branches || []).map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_gender">Gênero</Label>
                        <select id="emp_gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm" data-testid="emp-gender-select">
                          {GENDERS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_race">Raça/Cor <span className="text-amber-600">*</span></Label>
                        <select id="emp_race" value={form.race} onChange={(e) => setForm({ ...form, race: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                          {RACES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_age">Faixa etária</Label>
                        <select id="emp_age" value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                          <option value="">Selecione...</option>
                          {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_education">Escolaridade</Label>
                        <select id="emp_education" value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                          {EDUCATIONS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_dept">Setor / Depto</Label>
                        <Input id="emp_dept" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_position">Cargo</Label>
                        <Input id="emp_position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_level">Nível hierárquico</Label>
                        <select id="emp_level" value={form.hierarchy_level} onChange={(e) => setForm({ ...form, hierarchy_level: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                          <option value="">Selecione...</option>
                          {LEVELS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emp_hire">Data de admissão</Label>
                        <Input id="emp_hire" type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
                      </div>
                      <div className="flex items-center gap-2 md:col-span-2 pt-2">
                        <input type="checkbox" id="is_leader" checked={form.is_leadership} onChange={(e) => setForm({ ...form, is_leadership: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="emp-leader-check" />
                        <Label htmlFor="is_leader" className="cursor-pointer text-sm">É cargo de liderança</Label>
                      </div>
                      <div className="flex items-center gap-2 md:col-span-2 pt-2">
                        <input type="checkbox" id="has_pcd" checked={form.has_disability} onChange={(e) => setForm({ ...form, has_disability: e.target.checked })} className="w-4 h-4 accent-teal-600" data-testid="emp-pcd-check" />
                        <Label htmlFor="has_pcd" className="cursor-pointer text-sm">Pessoa com deficiência (dado sensível)</Label>
                      </div>
                      {form.has_disability && (
                        <div className="space-y-2 md:col-span-3">
                          <Label htmlFor="disability_type">Tipo de deficiência</Label>
                          <select id="disability_type" value={form.disability_type} onChange={(e) => setForm({ ...form, disability_type: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
                            <option value="">Selecione...</option>
                            {DISABILITY_TYPES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                          </select>
                        </div>
                      )}
                      {!editing && (
                        <div className="flex items-start gap-2 md:col-span-3 pt-2 border-t bg-amber-50 -mx-6 px-6 py-3">
                          <input type="checkbox" id="lgpd" checked={form.lgpd_consent} onChange={(e) => setForm({ ...form, lgpd_consent: e.target.checked })} className="w-4 h-4 mt-1 accent-amber-600" data-testid="lgpd-consent-check" required />
                          <Label htmlFor="lgpd" className="cursor-pointer text-xs text-amber-900">
                            Confirmo que obtive <strong>consentimento explícito e por escrito</strong> deste funcionário para armazenar seus dados pessoais e sensíveis conforme a LGPD.
                          </Label>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                      <Button type="submit" data-testid="emp-save-button">{editing ? 'Atualizar' : 'Criar'}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </CardContent>
      </Card>

      {!selectedCompany ? (
        <Card><CardContent className="p-12 text-center text-gray-500">Selecione uma empresa para começar.</CardContent></Card>
      ) : loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div></div>
      ) : (
        <>
          {/* Analytics */}
          {analytics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="analytics-cards">
                <StatCard icon={Users} label="Total" value={analytics.total_employees} hint="funcionários cadastrados" />
                <StatCard icon={Award} label="Liderança" value={analytics.leadership.total} hint={`${analytics.leadership.percentage}% do total`} color="amber" />
                <StatCard icon={Accessibility} label="PcD" value={analytics.disability.total} hint={`${analytics.disability.percentage}% do total`} color="blue" />
                <StatCard icon={Scale} label="Lei de Cotas" value={`${analytics.legal_quota.hired}/${analytics.legal_quota.required}`} hint={quotaLabel[analytics.legal_quota.compliance]} color={analytics.legal_quota.compliance === 'compliant' ? 'emerald' : 'red'} />
              </div>

              {analytics.total_employees > 0 && (
                <Card className="mb-6" data-testid="analytics-charts">
                  <CardContent className="p-6">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${quotaColor[analytics.legal_quota.compliance]} mb-4`}>
                      <Scale size={12} /> Lei de Cotas 8213/91: {quotaLabel[analytics.legal_quota.compliance]}
                      {analytics.legal_quota.gap > 0 && <span> · faltam {analytics.legal_quota.gap} PcD</span>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <DistributionBar label="Distribuição por gênero" data={analytics.gender_distribution} labelMap={{ male: 'Masculino', female: 'Feminino', other: 'Outro', not_informed: 'Não informado' }} />
                      <DistributionBar label="Distribuição por raça/cor" data={analytics.race_distribution} labelMap={{ white: 'Branca', black: 'Preta', brown: 'Parda', yellow: 'Amarela', indigenous: 'Indígena', not_informed: 'Não informado' }} />
                      <DistributionBar label="Liderança por gênero (M/F)" data={analytics.leadership.by_gender} labelMap={{ male: 'M', female: 'F', other: 'Outro', not_informed: 'N/I' }} />
                      <DistributionBar label="PcD por gênero (M/F)" data={analytics.disability.by_gender} labelMap={{ male: 'M', female: 'F', other: 'Outro', not_informed: 'N/I' }} />
                      <DistributionBar label="PcD por tipo" data={analytics.disability.by_type} labelMap={Object.fromEntries(DISABILITY_TYPES.map((d) => [d.v, d.l]))} />
                      <DistributionBar label="PcD por setor" data={analytics.disability.by_department} />
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Employee table */}
          <Card>
            <CardContent className="p-4 md:p-6">
              {employees.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <Users size={40} className="mx-auto text-gray-300 mb-3" />
                  Nenhum funcionário. Adicione manualmente ou importe via CSV.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Cargo / Depto</TableHead>
                      <TableHead>Nível</TableHead>
                      <TableHead className="text-center">Liderança</TableHead>
                      <TableHead className="text-center">PcD</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((e) => (
                      <TableRow key={e.id} data-testid={`emp-row-${e.id}`}>
                        <TableCell>
                          <div className="font-mono text-xs">{e.internal_code || '-'}</div>
                          {e.name && <div className="text-xs text-gray-500">{e.name}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{e.position || '-'}</div>
                          <div className="text-xs text-gray-500">{e.department || '-'}</div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {LEVELS.find((l) => l.v === e.hierarchy_level)?.l || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {e.is_leadership && <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">Sim</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          {e.has_disability && (
                            <span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold">
                              {DISABILITY_TYPES.find((d) => d.v === e.disability_type)?.l || 'Sim'}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(e)} data-testid={`edit-emp-${e.id}`}><Edit className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteId(e.id)} data-testid={`delete-emp-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>Esta ação é permanente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSidebar>
  );
}
